<template>
    <v-container fluid class="match-page-container px-0">
        <div
            class="match-sticky-header"
            :style="stickyHeaderStyle"
            :class="{
                'match-sticky-header-desktop': isDesktopHeaderEnabled,
                'match-header-compact': isDesktopHeaderEnabled && isHeaderCompact
            }"
        >
            <match-header
                :match="match"
                :summary="summary"
                :live="isLive"
                :compact="isDesktopHeaderEnabled && isHeaderCompact"
                @download="downloadStats"
            />
            <v-row v-if="!$vuetify.breakpoint.mobile" class="match-tabs-row">
                <v-col cols="12" class="mx-0 px-0">
                    <v-tabs
                        v-model="tab"
                        centered
                        class="match-tabs"
                        :class="{ 'match-tabs-compact': isDesktopHeaderEnabled && isHeaderCompact }"
                    >
                        <v-tabs-slider class="custom-orange" />
                        <v-tab v-for="(item, index) in tabItems" :key="index">
                            <v-icon small left>{{ item.icon }}</v-icon>
                            {{ item.name }}
                        </v-tab>
                    </v-tabs>
                </v-col>
            </v-row>
        </div>

        <!-- Mobile: app-style segmented control, sticky under the app header -->
        <nav v-if="$vuetify.breakpoint.mobile" class="match-segments">
            <div class="match-segments__track" :style="{ gridTemplateColumns: `repeat(${tabItems.length}, minmax(0, 1fr))` }">
                <button
                    v-for="(item, index) in tabItems"
                    :key="index"
                    type="button"
                    class="match-segments__btn"
                    :class="{ 'match-segments__btn--active': tab === index }"
                    @click="tab = index"
                >
                    <v-icon small>{{ item.icon }}</v-icon>
                    <span>{{ item.short || item.name }}</span>
                </button>
            </div>
        </nav>

        <div class="match-content-wrapper">
            <!-- the video sits above every tab, so it keeps playing whatever is open -->
            <match-video v-if="match.videoUrl" :url="match.videoUrl" :live="isLive" />
            <!-- a played match: its result and its MVP as pictures for the socials -->
            <match-share-images v-if="matchLoaded && hasScore && !isLive && match.team1 && match.team2" :match="match" :match-id="$route.params.id" :logs="matchLogs" />

            <!-- ---------- Upcoming match: preview ---------- -->
            <template v-if="tab === 0 && isUpcoming">
                <div class="match-section-title">
                    <span class="match-section-title__bar custom-orange" />
                    Πριν τον αγώνα
                </div>
                <div class="preview-grid">
                    <div class="preview-card preview-card--when">
                        <div class="preview-card__label">Πότε & πού</div>
                        <div class="preview-when">
                            <v-icon color="orange darken-2">mdi-calendar-outline</v-icon>
                            <span>{{ previewDate }}</span>
                        </div>
                        <a
                            v-if="summary && summary.arena"
                            class="preview-when preview-when--link"
                            :href="summary.arena.coordinates ? `https://www.google.com/maps/search/?api=1&query=${summary.arena.coordinates}` : undefined"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <v-icon color="orange darken-2">mdi-basketball-hoop-outline</v-icon>
                            <span>{{ summary.arena.name }}</span>
                        </a>
                        <!-- only for the captain of one of the two teams, as picked on the profile -->
                        <postponement-request v-if="summary" :match="calendarMatch" />
                        <match-change-request-button v-if="summary" :match="calendarMatch" />
                    </div>

                    <div v-for="side in previewTeams" :key="side.key" class="preview-card preview-team">
                        <router-link :to="{ name: 'Team', params: { id: side.team.id } }" class="preview-team__head">
                            <span class="preview-team__logo">
                                <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                                <v-icon v-else color="orange">mdi-basketball</v-icon>
                            </span>
                            <span class="preview-team__name">{{ side.team.name }}</span>
                        </router-link>
                        <div v-if="side.row" class="preview-team__stats">
                            <div class="preview-stat preview-stat--dark">
                                <span class="preview-stat__value">{{ side.row.position }}<small>η</small></span>
                                <span class="preview-stat__label">θέση</span>
                            </div>
                            <div class="preview-stat">
                                <span class="preview-stat__value">{{ side.row.won || 0 }}-{{ side.row.lost || 0 }}</span>
                                <span class="preview-stat__label">Ν-Η</span>
                            </div>
                            <div class="preview-stat">
                                <span class="preview-stat__value">{{ side.row.standing || 0 }}</span>
                                <span class="preview-stat__label">βαθμοί</span>
                            </div>
                        </div>
                        <div v-if="side.form.length" class="preview-team__form">
                            <span class="preview-stat__label">Φόρμα</span>
                            <span
                                v-for="(result, index) in side.form"
                                :key="index"
                                class="preview-form"
                                :class="result === 'W' ? 'is-win' : 'is-loss'"
                            >{{ result }}</span>
                        </div>
                        <div v-if="!side.row" class="preview-team__empty">Δεν υπάρχουν ακόμα αποτελέσματα σε αυτή τη φάση</div>
                    </div>
                </div>
            </template>

            <!-- ---------- Played / live match ---------- -->
            <template v-else-if="tab === 0">
                <match-period-score
                    v-if="match.team1 && match.team2"
                    :team1="match.team1"
                    :team2="match.team2"
                    :period-scores-team1="getPeriodPoints(match.team1)"
                    :period-scores-team2="getPeriodPoints(match.team2)"
                    :score1="Number(match.team1Score) || null"
                    :score2="Number(match.team2Score) || null"
                    :live="isLive"
                    :current-period="currentPeriod"
                />

                <template v-if="match.team1 && match.team2">
                    <div class="match-section-title">
                        <span class="match-section-title__bar custom-orange" />
                        Κορυφαίοι παίκτες
                    </div>
                    <div class="top-players">
                        <div
                            v-for="side in topPlayers"
                            :key="side.key"
                            class="top-player"
                            :class="{ 'top-player--clickable': side.player.id }"
                            :style="{ '--team-color': readableColor(side.team.color) }"
                            @click="goToPlayer(side.player.id)"
                        >
                            <div class="top-player__photo">
                                <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" class="top-player__watermark" alt="">
                                <img :src="side.player.image" :alt="side.player.name" class="top-player__img" @error="$event.target.src = defaultPlayerImg">
                            </div>
                            <div class="top-player__info">
                                <span class="top-player__label">MVP · {{ side.team.name }}</span>
                                <span class="top-player__name">{{ side.player.name }}</span>
                                <template v-if="side.player.hasPositivePir">
                                    <span class="top-player__pir">{{ side.player.pir }} <small>PIR</small></span>
                                    <span class="top-player__line">
                                        {{ side.player.points }} πόντοι · {{ side.player.rebounds }} ριμπ. · {{ side.player.assists }} ασίστ
                                    </span>
                                </template>
                                <span v-else class="top-player__line">Δεν υπάρχει ακόμα παίκτης με θετικό PIR</span>
                            </div>
                        </div>
                    </div>

                    <div class="match-section-title">
                        <span class="match-section-title__bar custom-orange" />
                        Σύγκριση ομάδων
                    </div>
                    <div class="match-two-cols">
                        <leading-players-graph :match="match" />
                        <main-match-stats-info :match="match" />
                    </div>
                </template>

                <div class="match-section-title">
                    <span class="match-section-title__bar custom-orange" />
                    Εξέλιξη σκορ
                </div>
                <div class="match-panel">
                    <score-evolution-chart
                        :logs="matchLogs"
                        :match="match"
                        :team1="match.team1"
                        :team2="match.team2"
                    />
                </div>

                <div class="match-section-title">
                    <span class="match-section-title__bar custom-orange" />
                    Σουτ
                </div>
                <div class="match-panel">
                    <advanced-shooting-chart :match="match" />
                </div>

                <div class="match-section-title">
                    <span class="match-section-title__bar custom-orange" />
                    Η ιστορία του αγώνα
                </div>
                <div class="match-panel">
                    <match-storyline
                        :logs="matchLogs"
                        :match="match"
                        :team1-data="match.team1"
                        :team2-data="match.team2"
                        :dense-export="$route.query.storylineExport === '1' || $route.query.print === '1'"
                    />
                </div>
            </template>

            <main-match-stats-player v-else-if="tab === 1" :match="match" />

            <advanced-analytics v-else-if="tab === 2 && hasAdvancedAnalytics" :match="match" :logs="matchLogs" />
        </div>
    </v-container>
</template>

<script>
    import MatchHeader from '@/components/MatchHeader'
    import PostponementRequest from '@/components/PostponementRequest'
    import MatchChangeRequestButton from '@/components/MatchChangeRequestButton'
    import MatchVideo from '@/components/MatchVideo'
    import MatchShareImages from '@/components/MatchShareImages'
    import { readableOn } from '@/utils/colors'
    import MainMatchStatsPlayer from '@/components/MainMatchStatsPlayer'
    import MainMatchStatsInfo from '@/components/MainMatchStatsInfo'
    import matchService from '@/services/match'
    import momentTimezone from 'moment-timezone'
    import AdvancedShootingChart from '@/components/AdvancedShootingChart'
    import MatchStoryline from '@/components/MatchStoryline'
    import MatchPeriodScore from '@/components/MatchPeriodScore'
    import ScoreEvolutionChart from '@/components/ScoreEvolutionChart'
    import LeadingPlayersGraph from '@/components/LeadingPlayersGraph'
    import AdvancedAnalytics from '@/components/AdvancedAnalytics'
    import { buildAdvancedAnalytics } from '@/utils/advancedAnalytics'
    import teamService from '@/services/team'
    import { getStateFromLogs, LIVE_WINDOW_MINUTES, CHECK_AFTER_START_MINUTES } from '@/utils/matchLive'
    import { prepareRanking, getLastFiveForm, getTeamId } from '@/utils/standings'

    const MINUTE = 60 * 1000
    // while a match can still be live, refresh it this often
    const LIVE_REFRESH_MS = 10 * 1000

    export default {
        components: { MatchHeader, PostponementRequest, MatchChangeRequestButton, MatchVideo, MatchShareImages, MainMatchStatsPlayer, MainMatchStatsInfo, AdvancedShootingChart, MatchStoryline, MatchPeriodScore, ScoreEvolutionChart, LeadingPlayersGraph, AdvancedAnalytics },
        data() {
            return {
                tab: 0,
                polling: null,
                items: [
                    {
                        name: 'Αγώνας',
                        icon: 'mdi-basketball'
                    },
                    {
                        name: 'Παίκτες',
                        icon: 'mdi-account-group-outline'
                    }
                ],
                match: {},
                // list entry of the match: pending / postponed / arena, missing from the stats payload
                summary: null,
                now: Date.now(),
                // ranking rows of the two teams, for the preview of an upcoming match
                previewRanking: { team1: null, team2: null },
                matchLogs: [],
                matchLoaded: false,
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                isHeaderCompact: false,
                compactEnterThreshold: 20,
                compactExitThreshold: 2,
                scrollRafId: null,
                headerToggleCooldownMs: 350,
                headerToggleLockedUntil: 0
            }
        },
        computed: {
            isDesktopHeaderEnabled() {
                return !this.$vuetify.breakpoint.mobile
            },
            stickyHeaderStyle() {
                if (!this.isDesktopHeaderEnabled) {
                    return {}
                }
                return {
                    top: '60px'
                }
            },
            // the advanced analytics tab only for a played match with something to analyse
            hasAdvancedAnalytics() {
                return this.matchLoaded && !this.isUpcoming && buildAdvancedAnalytics(this.match, this.matchLogs).available
            },
            tabItems() {
                return this.hasAdvancedAnalytics
                    ? this.items.concat({ name: 'Advanced Analytics', short: 'Analytics', icon: 'mdi-chart-timeline-variant-shimmer' })
                    : this.items
            },
            startTime() {
                return this.match.matchDatetime ? momentTimezone(this.match.matchDatetime).valueOf() : null
            },
            // around tip-off and until a few hours later the match may change, so the page keeps refreshing
            inLiveWindow() {
                if (!this.startTime || (this.summary && this.summary.postponed)) return false
                return this.now >= this.startTime - 15 * MINUTE && this.now < this.startTime + CHECK_AFTER_START_MINUTES * MINUTE
            },
            // same rule as the schedule: the stat logs tell whether "end game" was pressed
            logsState() {
                return getStateFromLogs(this.matchLogs)
            },
            // the latest period in the play-by-play, for the live marker of the period score
            currentPeriod() {
                const periods = (this.matchLogs || []).map(log => Number(log && log.period)).filter(period => Number.isFinite(period) && period > 0)
                return periods.length ? Math.max(...periods) : null
            },
            isLive() {
                if (!this.inLiveWindow) return false
                if (this.logsState === 'live') return true
                if (this.logsState === 'ended') return false
                return this.now >= this.startTime && this.now < this.startTime + LIVE_WINDOW_MINUTES * MINUTE
            },
            hasScore() {
                return (Number(this.match.team1Score) || 0) > 0 || (Number(this.match.team2Score) || 0) > 0
            },
            isUpcoming() {
                if (!this.matchLoaded || this.isLive || this.hasScore) return false
                if (this.summary) return !!this.summary.pending || !!this.summary.postponed
                return !!this.startTime && this.startTime > this.now
            },
            previewDate() {
                if (!this.match.matchDatetime) return 'Δεν έχει οριστεί ημερομηνία'
                return momentTimezone(this.match.matchDatetime).tz('Europe/Athens').locale('el').format('dddd D MMMM · HH:mm')
            },
            calendarMatch() {
                return { ...this.summary, team1: this.match.team1 || this.summary.team1, team2: this.match.team2 || this.summary.team2 }
            },
            previewTeams() {
                return [
                    { key: 'team1', team: this.match.team1 || {} },
                    { key: 'team2', team: this.match.team2 || {} }
                ].filter(side => side.team.id).map(side => {
                    const row = this.previewRanking[side.key]
                    return { ...side, row, form: row ? getLastFiveForm(row) : [] }
                })
            },
            topPlayers() {
                return [
                    { key: 'team1', team: this.match.team1 || {}, player: this.getTopPirPlayer(this.match.team1) },
                    { key: 'team2', team: this.match.team2 || {}, player: this.getTopPirPlayer(this.match.team2) }
                ]
            }
        },
        watch: {
            // the logs come after the match: a link straight to the analytics tab opens it once it exists
            hasAdvancedAnalytics() {
                this.applyTabFromRoute()
            },
            '$route.params.id': {
                handler() {
                    this.matchLoaded = false
                    this.applyTabFromRoute()
                    this.summary = null
                    this.previewRanking = { team1: null, team2: null }
                    this.loadMatch()
                }
            },
            isUpcoming(upcoming) {
                if (upcoming) {
                    this.fetchPreviewRanking()
                }
            },
            '$route.query.tab': {
                handler() {
                    this.applyTabFromRoute()
                }
            },
            tab: {
                handler(newTab) {
                    this.syncTabToRoute(newTab)
                }
            },
            '$vuetify.breakpoint.mobile': {
                handler(isMobile) {
                    if (isMobile) {
                        this.isHeaderCompact = false
                    } else {
                        this.handleHeaderScroll()
                    }
                }
            }
        },
        beforeDestroy() {
            clearInterval(this.polling)
            window.removeEventListener('scroll', this.onScroll)
            if (this.scrollRafId) {
                window.cancelAnimationFrame(this.scrollRafId)
                this.scrollRafId = null
            }
        },
        mounted() {
            this.handleHeaderScroll()
            window.addEventListener('scroll', this.onScroll, { passive: true })
        },
        created() {
            this.applyTabFromRoute()
            this.loadMatch()
            // Refresh while the match can be live; once "end game" is in the logs there is nothing left to update
            this.polling = setInterval(() => {
                this.now = Date.now()
                if (this.inLiveWindow && this.logsState !== 'ended') {
                    this.refreshLive()
                }
            }, LIVE_REFRESH_MS)
        },
        methods: {
            // a team colour that shows on the white cards
            readableColor(color) {
                return readableOn(color || '#ff7133')
            },
            loadMatch() {
                const matchId = this.$route.params.id
                matchService.getMatchSummary(matchId).then(summary => {
                    if (String(matchId) === String(this.$route.params.id)) {
                        this.summary = summary
                    }
                }).catch(() => {})
                return this.fetchMatch(matchId).then(() => {
                    this.fetchMatchLogs(matchId)
                    this.applyTabFromRoute()
                })
            },
            refreshLive() {
                const matchId = this.$route.params.id
                this.fetchMatch(matchId)
                this.fetchMatchLogs(matchId)
                matchService.getMatchSummary(matchId).then(summary => {
                    if (summary) this.summary = summary
                }).catch(() => {})
            },
            fetchPreviewRanking() {
                const phase = this.match.cup ? 'cup' : this.match.phase
                ;['team1', 'team2'].forEach(key => {
                    const team = this.match[key]
                    if (!team || !team.id) return
                    teamService.getRankingByTeam(team.id, phase).then(resp => {
                        const row = prepareRanking(resp.data).find(item => String(getTeamId(item)) === String(team.id)) || null
                        this.$set(this.previewRanking, key, row)
                    }).catch(() => {})
                })
            },
            setHeaderCompact(nextValue) {
                if (this.isHeaderCompact === nextValue) {
                    return
                }

                const now = Date.now()
                if (now < this.headerToggleLockedUntil) {
                    return
                }

                this.isHeaderCompact = nextValue
                this.headerToggleLockedUntil = now + this.headerToggleCooldownMs
            },
            onScroll() {
                if (this.scrollRafId) {
                    return
                }
                this.scrollRafId = window.requestAnimationFrame(() => {
                    this.scrollRafId = null
                    this.handleHeaderScroll()
                })
            },
            handleHeaderScroll() {
                const scrollY = Math.max(0, window.scrollY || window.pageYOffset || 0)

                if (!this.isDesktopHeaderEnabled) {
                    this.isHeaderCompact = false
                    return
                }

                if (scrollY <= this.compactExitThreshold) {
                    this.setHeaderCompact(false)
                } else if (scrollY >= this.compactEnterThreshold) {
                    this.setHeaderCompact(true)
                }
            },
            getTopPirPlayer(team = {}) {
                const players = Array.isArray(team?.players) ? team.players : []
                const empty = {
                    id: null,
                    name: 'Δεν υπάρχει ακόμα',
                    pir: null,
                    image: this.defaultPlayerImg,
                    hasPositivePir: false
                }
                if (!players.length) {
                    return empty
                }

                const playersWithPositivePir = players.filter(player => this.getPlayerPir(player) > 0)
                if (!playersWithPositivePir.length) {
                    return empty
                }

                const top = playersWithPositivePir.reduce((best, player) => {
                    const candidatePir = this.getPlayerPir(player)
                    const bestPir = this.getPlayerPir(best)
                    return candidatePir > bestPir ? player : best
                }, playersWithPositivePir[0])

                const stats = (top.stats && top.stats[0]) || {}
                return {
                    id: this.getPlayerId(top),
                    name: this.getTopPirDisplayName(top),
                    pir: this.getPlayerPir(top),
                    image: this.getPlayerImageSrc(top),
                    points: Number(stats.points) || 0,
                    rebounds: Number(stats.rebounds) || 0,
                    assists: Number(stats.assists) || 0,
                    hasPositivePir: true
                }
            },
            getPlayerId(player = {}) {
                return player?.id || player?.playerId || player?.athleteId || player?.player?.id || player?.athlete?.id || null
            },
            goToPlayer(playerId) {
                if (!playerId) return
                this.$router.push({ name: 'Player', params: { id: playerId } })
            },
            getPlayerPir(player = {}) {
                const stats = player?.stats?.[0] || {}
                const direct = Number(stats.pir)
                if (Number.isFinite(direct)) return Math.round(direct)

                const madeFt = Number(stats.freeThrowsMade) || 0
                const attFt = Number(stats.freeThrowAttempts) || 0
                const made2 = Number(stats.twoPointsMade) || 0
                const att2 = Number(stats.twoPointAttempts) || 0
                const made3 = Number(stats.threePointsMade) || 0
                const att3 = Number(stats.threePointAttempts) || 0
                const points = Number(stats.points) || 0
                const rebounds = Number(stats.rebounds) || 0
                const assists = Number(stats.assists) || 0
                const steals = Number(stats.steals) || 0
                const blocks = Number(stats.blockedShots) || 0
                const turnovers = Number(stats.turnovers) || 0
                const fouls = Number(stats.personalFouls) || 0

                return Math.round(points + rebounds + assists + steals + blocks - turnovers - (attFt - madeFt) - (att2 - made2) - (att3 - made3) - fouls)
            },
            getPlayerName(player = {}) {
                if (player?.name) return String(player.name).toUpperCase()
                const athlete = player?.athleteName || player?.playerName || player?.player?.name || null
                if (athlete) return String(athlete).toUpperCase()
                const jersey = player?.alternativeJersey ?? player?.jersey
                if (jersey !== null && jersey !== undefined && jersey !== '') return `#${jersey}`
                return 'NO PLAYER'
            },
            getTopPirDisplayName(player = {}) {
                const jersey = this.getPlayerJersey(player)
                const fullName = this.getPlayerFullName(player)
                const camelFullName = this.toCamelCase(fullName || 'Player')

                if (jersey) {
                    return `#${jersey} ${camelFullName}`
                }

                return camelFullName || 'No Player'
            },
            getPlayerJersey(player = {}) {
                const jersey = player?.alternativeJersey ?? player?.jersey ?? player?.player?.jersey ?? player?.athlete?.jersey
                if (jersey === null || jersey === undefined || jersey === '') return null
                return String(jersey).trim()
            },
            getPlayerLastName(player = {}) {
                const directLastName = player?.lastName || player?.surname || player?.familyName || player?.athleteLastName || player?.player?.lastName || player?.athlete?.lastName
                if (directLastName) {
                    return String(directLastName).trim()
                }

                const fullName = player?.name || player?.athleteName || player?.playerName || player?.player?.name || player?.athlete?.name || ''
                const normalized = String(fullName || '').trim()
                if (!normalized) return ''

                if (normalized.includes(',')) {
                    return normalized.split(',')[0].trim()
                }

                const parts = normalized.split(/\s+/)
                return parts[parts.length - 1] || normalized
            },
            getPlayerFirstName(player = {}) {
                const directFirstName = player?.firstName || player?.givenName || player?.athleteFirstName || player?.player?.firstName || player?.athlete?.firstName
                if (directFirstName) {
                    return String(directFirstName).trim()
                }

                const fullName = player?.name || player?.athleteName || player?.playerName || player?.player?.name || player?.athlete?.name || ''
                const normalized = String(fullName || '').trim()
                if (!normalized) return ''

                if (normalized.includes(',')) {
                    const rightSide = normalized.split(',')[1] || ''
                    return rightSide.trim().split(/\s+/)[0] || ''
                }

                return normalized.split(/\s+/)[0] || ''
            },
            getPlayerFullName(player = {}) {
                const firstName = this.getPlayerFirstName(player)
                const lastName = this.getPlayerLastName(player)

                if (firstName && lastName) {
                    return `${lastName} ${firstName}`
                }

                if (firstName) return firstName
                if (lastName) return lastName

                const fallback = player?.name || player?.athleteName || player?.playerName || player?.player?.name || player?.athlete?.name || ''
                return String(fallback).trim()
            },
            toCamelCase(value = '') {
                return String(value)
                    .trim()
                    .toLocaleLowerCase()
                    .split(/([\s-']+)/)
                    .map(part => {
                        if (!part || /^[\s-']+$/.test(part)) return part
                        return part.charAt(0).toLocaleUpperCase() + part.slice(1)
                    })
                    .join('')
            },
            getPlayerImageSrc(player = {}) {
                const raw = player.image || player.photo || player.avatar || player.imageUrl || player.player?.image || player.athlete?.image || null
                if (!raw) return this.defaultPlayerImg
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            // an old link to the removed video tab (?tab=2) falls back to the first tab
            getMaxTabIndex() {
                return this.tabItems.length - 1
            },
            getRouteTab() {
                const parsedTab = Number.parseInt(this.$route.query.tab, 10)
                const maxTab = this.getMaxTabIndex()
                if (Number.isNaN(parsedTab) || parsedTab < 0 || parsedTab > maxTab) {
                    return 0
                }
                return parsedTab
            },
            applyTabFromRoute() {
                const routeTab = this.getRouteTab()
                if (this.tab !== routeTab) {
                    this.tab = routeTab
                }
            },
            syncTabToRoute(tab) {
                if (tab === null || tab === undefined) {
                    return
                }
                const routeTab = this.getRouteTab()
                if (tab === routeTab && this.$route.query.tab !== undefined) {
                    return
                }
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        tab: String(tab)
                    }
                }).catch(() => {})
            },
            getPeriodPoints(team) {
                if (!team || !team.players) {
                    return { 1: '-', 2: '-', 3: '-', 4: '-' }
                }
                const periodPoints = {}
                team.players.forEach(player => {
                    if (
                        player.stats &&
                        player.stats.length > 0 &&
                        player.stats[0] &&
                        player.stats[0].periodScore
                    ) {
                        Object.entries(player.stats[0].periodScore).forEach(([period, points]) => {
                            periodPoints[period] = (periodPoints[period] || 0) + points
                        })
                    }
                })
                // Fill missing periods with '-'
                for (let i = 1; i <= 4; i++) {
                    if (periodPoints[i] === undefined) periodPoints[i] = '-'
                }
                return periodPoints
            },
            fetchMatch(matchId) {
                return matchService.getMatch(matchId).then(resp => {
                    this.match = resp.data
                    this.matchLoaded = true
                    this.setMatchPageMeta(this.match)
                })
            },
            upsertMetaTag(selector, attrs) {
                let el = document.head.querySelector(selector)
                if (!el) {
                    el = document.createElement('meta')
                    document.head.appendChild(el)
                }

                Object.keys(attrs).forEach(key => {
                    el.setAttribute(key, attrs[key])
                })
            },
            resolveImageUrl(raw) {
                if (!raw) return null
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            getMatchMetaImages(match = {}) {
                const team1Logo = this.resolveImageUrl(match?.team1?.logo || match?.team1?.image || null)
                const team2Logo = this.resolveImageUrl(match?.team2?.logo || match?.team2?.image || null)
                const leagueLogo = this.resolveImageUrl(match?.league?.logo || null)
                const fallback = `${window.location.origin}/icon-512.png`

                return [team1Logo, team2Logo, leagueLogo, fallback].filter(Boolean)
            },
            setMetaTagList(property, values, key) {
                const attr = 'data-dynamic-meta'
                const selector = `meta[${attr}^="${key}-"]`
                document.head.querySelectorAll(selector).forEach(node => node.remove())

                values.forEach((value, index) => {
                    const el = document.createElement('meta')
                    el.setAttribute('property', property)
                    el.setAttribute('content', value)
                    el.setAttribute(attr, `${key}-${index}`)
                    document.head.appendChild(el)
                })
            },
            getMatchTeamsLabel(match = {}) {
                const team1Name = String(match?.team1?.name || 'Team 1').trim()
                const team2Name = String(match?.team2?.name || 'Team 2').trim()
                return `${team1Name} vs ${team2Name}`
            },
            getMatchScoreLabel(match = {}) {
                const score1 = Number(match?.scoreTeam1)
                const score2 = Number(match?.scoreTeam2)
                const hasScore = Number.isFinite(score1) && Number.isFinite(score2) && (score1 > 0 || score2 > 0)

                return hasScore ? `${score1}-${score2}` : 'VS'
            },
            getMatchDateLabel(match = {}) {
                if (!match?.matchDatetime) return ''
                return momentTimezone(match.matchDatetime).tz('Europe/Athens').locale('el-GR').format('dddd D/M/y, HH:mm').toString()
            },
            getMatchCourtLabel(match = {}) {
                return String(
                    match?.court?.title ||
                    match?.court?.name ||
                    match?.courtTitle ||
                    match?.courtName ||
                    ''
                ).trim()
            },
            setMatchPageMeta(match = {}) {
                const siteName = 'BIG Basketball'
                const teamsLabel = this.getMatchTeamsLabel(match)
                const scoreLabel = this.getMatchScoreLabel(match)
                const dateLabel = this.getMatchDateLabel(match)
                const courtLabel = this.getMatchCourtLabel(match)
                const title = `${teamsLabel} | ${scoreLabel} | ${siteName}`
                const descriptionParts = [teamsLabel, dateLabel, courtLabel ? `Court: ${courtLabel}` : '', `Score: ${scoreLabel}`].filter(Boolean)
                const description = descriptionParts.join(' | ')
                const images = this.getMatchMetaImages(match)
                const primaryImage = images[0] || `${window.location.origin}/icon-512.png`
                const url = window.location.href

                document.title = title

                this.upsertMetaTag('meta[name="description"]', { name: 'description', content: description })
                this.upsertMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: siteName })
                this.upsertMetaTag('meta[property="og:title"]', { property: 'og:title', content: title })
                this.upsertMetaTag('meta[property="og:description"]', { property: 'og:description', content: description })
                this.setMetaTagList('og:image', images.slice(0, 2), 'match-og-image')
                this.upsertMetaTag('meta[property="og:url"]', { property: 'og:url', content: url })
                this.upsertMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'website' })
                this.upsertMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' })
                this.upsertMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
                this.upsertMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
                this.upsertMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: primaryImage })
            },
            fetchMatchLogs(matchId) {
                return matchService.getMatchLogs(matchId)
                    .then(resp => {
                        this.matchLogs = resp.data
                    })
                    .catch(() => {
                        this.matchLogs = []
                    })
            },
            downloadStats() {
                window.open(`${this.assetsUrl}/${this.match.pdfUrl}`, '_blank')
            }
        }
    }
</script>

<style scoped>
.match-page-container {
    position: relative;
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow-anchor: none;
    font-family: Ubuntu, Arial, sans-serif;
}

.match-sticky-header {
    position: relative;
    width: 100%;
    overflow-anchor: none;
}

.match-sticky-header-desktop {
    position: sticky;
    z-index: 9;
    background: transparent;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease;
}

.match-header-compact {
    box-shadow: none;
}

.match-tabs-row {
    margin-top: 0;
    padding: 0;
}

.match-tabs {
    max-width: 1200px;
    margin: 8px auto 0;
    background: transparent;
}

.match-tabs ::v-deep .v-tabs-bar {
    border-radius: 10px;
    background: #fff !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.v-tab {
    text-transform: none !important;
    font-weight: bolder;
    font-size: 15px;
    color: #757578 !important;
}

.v-tab .v-icon {
    color: inherit !important;
}

.match-tabs-compact /deep/ .v-tab {
    min-height: 40px;
    font-size: 13px;
}

.v-tab::before {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    background-color: transparent;
}

.v-tab--active {
    color: #ef4444 !important;
}

/* ---------- Mobile segmented tabs ---------- */
.match-segments {
    /* split in two: the build's CSS minifier cannot parse max() nested in calc() */
    --safe-top: max(env(safe-area-inset-top), var(--safe-area-inset-top, env(safe-area-inset-top)));

    position: sticky;
    top: calc(56px + var(--safe-top));
    z-index: 5;
    padding: 10px 12px 6px;
}

.match-segments__track {
    display: grid;
    gap: 4px;
    padding: 4px;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.match-segments__btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    min-width: 0;
    height: 48px;
    border-radius: 10px;
    color: #6b6b70;
    font-size: 0.72rem;
    font-weight: 700;
    -webkit-tap-highlight-color: transparent;
    transition: background 0.2s ease, color 0.2s ease;
}

.match-segments__btn span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.match-segments__btn .v-icon {
    color: inherit !important;
}

.match-segments__btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
    color: #fff;
    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.3);
}

/* ---------- Content ---------- */
.match-content-wrapper {
    max-width: 1224px;
    margin: 16px auto 0;
    padding: 0 12px 20px;
    text-align: left;
}

.match-section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 22px 0 12px;
    padding: 8px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 1.1rem;
    font-weight: 800;
}

.match-section-title__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.match-panel {
    padding: 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.match-two-cols {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    align-items: start;
}

/* ---------- Top players ---------- */
.top-players {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}

.top-player {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border-radius: 14px;
    background: #fff;
    border-left: 5px solid var(--team-color);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.top-player--clickable {
    cursor: pointer;
}

.top-player--clickable:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.14);
}

.top-player__photo {
    position: relative;
    flex: 0 0 100px;
    width: 100px;
    height: 116px;
}

.top-player__watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 175%;
    width: auto;
    max-width: none;
    transform: translate(-50%, -50%);
    opacity: 0.16;
    pointer-events: none;
}

.top-player__img {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.top-player__info {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
}

.top-player__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.top-player__name {
    margin-top: 2px;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
}

.top-player__pir {
    margin-top: 4px;
    background-image: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.1;
}

.top-player__pir small {
    font-size: 0.9rem;
}

.top-player__line {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.85rem;
    font-weight: 600;
}

/* ---------- Upcoming preview ---------- */
.preview-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
}

.preview-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    border-radius: 14px;
    background: #fff;
    border-top: 3px solid #ff7133;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.preview-card__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.preview-when {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #2b2b2e !important;
    font-weight: 700;
    text-decoration: none;
    text-transform: capitalize;
}

.preview-when--link:hover {
    color: #ef4444 !important;
}

.preview-team__head {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #2b2b2e !important;
    text-decoration: none;
}

.preview-team__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    padding: 4px;
    border-radius: 50%;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
}

.preview-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.preview-team__name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.25rem;
    font-weight: 800;
}

.preview-team__stats {
    display: flex;
    gap: 8px;
}

.preview-stat {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    padding: 6px;
    border-radius: 8px;
    background: #f4f4f6;
}

.preview-stat--dark {
    background: #0d0d0f;
    color: #fff;
}

.preview-stat__value {
    font-size: 1.2rem;
    font-weight: 800;
}

.preview-stat__value small {
    font-size: 0.65rem;
}

.preview-stat__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.preview-stat--dark .preview-stat__label {
    color: rgba(255, 255, 255, 0.7);
}

.preview-team__form {
    display: flex;
    align-items: center;
    gap: 4px;
}

.preview-team__form .preview-stat__label {
    margin-right: 4px;
}

.preview-form {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
}

.preview-form.is-win {
    background: #2e7d32;
}

.preview-form.is-loss {
    background: #c62828;
}

.preview-team__empty {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.85rem;
}

/* ---------- Responsive ---------- */
@media (max-width: 960px) {
    .match-two-cols,
    .top-players {
        grid-template-columns: minmax(0, 1fr);
    }

    .preview-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .match-tabs-compact /deep/ .v-tab {
        min-height: 36px;
        font-size: 12px;
        padding: 0 8px;
    }
}

@media (max-width: 600px) {
    .match-content-wrapper {
        margin-top: 4px;
    }

    .top-player__photo {
        flex-basis: 80px;
        width: 80px;
        height: 96px;
    }
}
</style>
